import { useMutation } from "@tanstack/react-query";
import { Link, useLocation } from "@tanstack/react-router";
import { Check, Circle, Eye, EyeOff } from "lucide-react";
import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";

import { ApiError, setPassword } from "../api/client";
import { oidcClient } from "../auth/oidc-config";
import { AuthShell } from "../components/AuthShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CHANNEL_PARTNER, FULL_LABEL, ROLE_BLURB, type ConceptualRole } from "../lib/role-labels";
import { FormError } from "@/components/ui/form-error";

/**
 * Set-password landing page (ADR-0010).
 *
 * Reached from the invitation/forgot-password email link. Reads the
 * recovery token from `?token=` and posts the new password to
 * `POST /auth/set-password`. The backend drives the `prm-recovery`
 * flow, completes the OIDC handshake using the SPA's
 * `oidc_authorize_url`, and returns a PRM-origin
 * `/auth/callback?code=...` URL — so the user lands signed-in
 * without a second prompt.
 */

const MIN_PASSWORD_LEN = 8;
// Authentik's recovery password policy (verified directly against the PROD
// policy object on 2026-06-24: length_min=8, check_zxcvbn=true,
// zxcvbn_score_threshold=2) rejects anything scoring below 2 on zxcvbn's 0–4
// scale. We mirror that gate client-side so a weak password never reaches the
// server — the recovery token is single-use and a rejected attempt burns the
// link (CHP-254).
//
// THIS CONSTANT MUST EQUAL Authentik's `zxcvbn_score_threshold`. They drifted:
// CHP-276 eased only this client value 2 → 1 while the server policy stayed at
// 2, so score-1 passwords passed here, were rejected server-side, and burned the
// single-use token — which the retry then surfaced as the misleading "reset link
// no longer valid" (the soft-launch invite-burn incident, 2026-06-24). Realigned
// to 2. If product wants an easier gate, change the Authentik policy in lockstep
// — never let these two diverge again.
//
// KNOWN RESIDUAL: this client mirror can't see the user's name/email, which
// Authentik ALSO feeds into zxcvbn. A password that's weak *only* because it
// contains the user's own name can still pass here and be rejected server-side
// → burned token. Fully closing that requires the backend to resolve the token
// to the user and pre-validate before driving the flow (RACCPE-19 "Option B" —
// store sha256(token) at invite time). The Authentik policy is the backstop.
const MIN_PASSWORD_SCORE = 2;
// Terms the password shouldn't be built around. This is a token-only page —
// we have no profile here — so we can only seed brand/product words, not the
// user's own name/email (Authentik's server-side check additionally seeds those).
const WEAK_PASSWORD_INPUTS = ["resetdata", "reset data", "partner", "password", "prm"];

// `zxcvbn` is a CommonJS `export =` module, so its type *is* the function.
// Vite's interop hands it back under `.default` at runtime, so the loader
// resolves default-or-self to cover both shapes.
type Zxcvbn = typeof import("zxcvbn");

/** Turn zxcvbn's feedback into a single actionable sentence. */
function weakPasswordMessage(feedback: { warning: string; suggestions: string[] }): string {
  const hint = feedback.warning || feedback.suggestions[0] || "";
  if (!hint) {
    return "Password is too weak — add another word or two; uncommon words are strongest.";
  }
  // Lower-case the first letter so it reads as a continuation of the sentence.
  return `Password is too weak — ${hint.charAt(0).toLowerCase()}${hint.slice(1)}`;
}

export function SetPasswordRoute() {
  // Read the recovery token directly from the router's location
  // searchStr — `useSearch` requires a validateSearch schema on the
  // route to round-trip unknown params, and we don't want to constrain
  // the token's shape with a Zod schema (it's an opaque Authentik
  // string).
  const { searchStr } = useLocation();
  const token = useMemo(() => {
    const qs = (searchStr || "").replace(/^\?/, "");
    return new URLSearchParams(qs).get("token") ?? "";
  }, [searchStr]);

  // CHP-250: invitation links carry the invitee's conceptual role (`&role=cm`)
  // so the activation screen can show it as a locked, read-only field. Narrow
  // to a known role — an unknown or absent value (e.g. forgot-password links,
  // which carry no role) renders nothing, preserving the prior behaviour. The
  // value is display-only; the authoritative role stays Authentik group
  // membership + backend RBAC.
  const role = useMemo<ConceptualRole | null>(() => {
    const qs = (searchStr || "").replace(/^\?/, "");
    const raw = new URLSearchParams(qs).get("role");
    return raw && raw in FULL_LABEL ? (raw as ConceptualRole) : null;
  }, [searchStr]);

  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [show1, setShow1] = useState(false);
  const [show2, setShow2] = useState(false);
  const [topError, setTopError] = useState<string | null>(null);
  // CHP-809: a server-side 422 (e.g. the password was weak ONLY because it
  // contained the user's own name — invisible to the token-only client mirror,
  // RACCPE-19) may have already burned the single-use recovery token. Surface a
  // path to request a fresh link rather than leaving the user stuck on a copy
  // line. Durable fix is backend pre-validation (RACCPE-19 "Option B").
  const [showFreshLink, setShowFreshLink] = useState(false);
  // Onboarding friction fix: live password-requirement state so the checklist
  // ticks turn green as each rule is met (no more submit-then-see-errors).
  // zxcvbn score is recomputed on every change; null = no input yet (neutral).
  const [pwScore, setPwScore] = useState<number | null>(null);
  const [pwHint, setPwHint] = useState("");

  const tokenMissing = !token;

  // zxcvbn ships a large dictionary, so load it as its own async chunk only
  // when this page is in use — never in the login bundle. We warm it on mount
  // while the user types; `loadZxcvbn()` is idempotent and also covers the
  // (impossible-in-practice) case of a submit before the effect runs.
  const zxcvbnRef = useRef<Promise<Zxcvbn> | null>(null);
  const loadZxcvbn = (): Promise<Zxcvbn> =>
    (zxcvbnRef.current ??= import("zxcvbn").then(
      (m) => (m as unknown as { default?: Zxcvbn }).default ?? (m as unknown as Zxcvbn),
    ));
  useEffect(() => {
    void loadZxcvbn();
  }, []);

  // Recompute the zxcvbn strength on every change so the "hard to guess" tick
  // reflects the current input. The dictionary lives in the async chunk; the
  // promise is idempotent (loadZxcvbn caches it), so this is cheap after the
  // first load. Empty input → no score (neutral, not a failed requirement).
  useEffect(() => {
    if (!pw1) {
      setPwScore(null);
      setPwHint("");
      return;
    }
    let cancelled = false;
    void loadZxcvbn().then((zxcvbn) => {
      if (cancelled) return;
      const result = zxcvbn(pw1, WEAK_PASSWORD_INPUTS);
      setPwScore(result.score);
      setPwHint(
        result.score < MIN_PASSWORD_SCORE
          ? result.feedback.warning || result.feedback.suggestions[0] || ""
          : "",
      );
    });
    return () => {
      cancelled = true;
    };
  }, [pw1]);

  const submit = useMutation({
    mutationFn: async () => {
      // request_type "si:r" — see login.tsx for why.
      const req = await oidcClient.createSigninRequest({ request_type: "si:r" });
      return setPassword({
        token,
        new_password: pw1,
        oidc_authorize_url: req.url,
      });
    },
    onSuccess: (resp) => {
      window.location.assign(resp.redirect_url);
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        // 422 = the link is fine but the password failed the policy
        // (too short / not strong enough). The backend sends the specific
        // reason as `detail`; show it so the user knows what to change.
        if (err.status === 422) {
          setTopError(
            detailMessage(err.body) ??
              "That password doesn't meet the requirements below. Try a longer or less common one.",
          );
          // CHP-809: the single-use token may already be spent by this attempt;
          // offer a path to a fresh link so the user isn't stranded.
          setShowFreshLink(true);
          return;
        }
        // 401 now means ONLY the link is dead — no longer conflated with a
        // weak password. CHP-1195: the backend distinguishes a replayed
        // (already-consumed) link so the user isn't left guessing why a
        // form that rendered fine won't go through.
        if (err.status === 401) {
          if (detailMessage(err.body) === "recovery token already used") {
            setTopError("That link has already been used. Request a new one to continue.");
            setShowFreshLink(true);
            return;
          }
          setTopError("That reset link is no longer valid. Request a new one to continue.");
          return;
        }
        if (err.status === 429) {
          setTopError("Too many attempts. Wait a minute and try again.");
          return;
        }
        if (err.status === 502) {
          setTopError(
            "We can't reach the authentication service right now. Please try again shortly.",
          );
          return;
        }
      }
      setTopError("Couldn't set your password. Try again.");
    },
  });

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setTopError(null);
    setShowFreshLink(false);
    if (pw1.length < MIN_PASSWORD_LEN) {
      setTopError(`Password must be at least ${MIN_PASSWORD_LEN} characters.`);
      return;
    }
    if (pw1 !== pw2) {
      setTopError("Passwords don't match.");
      return;
    }
    // Strength-gate before spending the single-use recovery token (CHP-254).
    const zxcvbn = await loadZxcvbn();
    const result = zxcvbn(pw1, WEAK_PASSWORD_INPUTS);
    if (result.score < MIN_PASSWORD_SCORE) {
      setTopError(weakPasswordMessage(result.feedback));
      return;
    }
    submit.mutate();
  }

  // Live requirement checks driving the checklist ticks + the submit gate.
  // strengthOk needs a computed score (null while empty/loading → not met).
  const lenOk = pw1.length >= MIN_PASSWORD_LEN;
  const strengthOk = pwScore !== null && pwScore >= MIN_PASSWORD_SCORE;
  const matchOk = pw2.length > 0 && pw1 === pw2;
  const allValid = lenOk && strengthOk && matchOk;

  if (tokenMissing) {
    return (
      <AuthShell headingId="set-heading" heading="Reset link missing" bodyClassName="space-y-4">
        <p className="text-sm text-muted-foreground">
          This page needs a reset token from the link in your email. Open the link from the email
          directly, or{" "}
          <Link to="/forgot-password" className="font-medium text-primary hover:underline">
            request a new one
          </Link>
          .
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      headingId="set-heading"
      heading="Set your password"
      subheading={
        <>
          Choose your password to access the ResetData {CHANNEL_PARTNER} Platform. You'll be signed
          in once it's saved.
        </>
      }
    >
      <form
        onSubmit={(e) => void onSubmit(e)}
        aria-busy={submit.isPending}
        noValidate
        className="space-y-4"
      >
        {role ? (
          <div className="space-y-2">
            <span
              id="set-role-label"
              className="block text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground"
            >
              Assigned Administrative Role
            </span>
            {/* Locked, display-only value (CHP-250) — rendered as static text,
                  not a disabled/readOnly input: disabled controls aren't
                  submitted and are skipped by assistive tech, and we never
                  submit this value. `aria-labelledby` ties it to its label. */}
            <p
              aria-labelledby="set-role-label"
              className="flex h-9 w-full items-center rounded-md border border-input bg-muted px-3 text-sm text-muted-foreground"
            >
              {FULL_LABEL[role]}
            </p>
            {/* CHP-507: role explainer (PO/BDM only; CM/RM/RA have no copy yet). */}
            {ROLE_BLURB[role] ? (
              <p className="text-xs text-muted-foreground">{ROLE_BLURB[role]}</p>
            ) : null}
          </div>
        ) : null}
        <div className="space-y-2">
          <PasswordField
            id="set-pw1"
            label="New password"
            value={pw1}
            onChange={setPw1}
            reveal={show1}
            onToggleReveal={() => setShow1((s) => !s)}
            autoFocus
            describedBy="set-pw-rules"
          />
          <ul
            id="set-pw-rules"
            className="space-y-1.5 text-xs"
            aria-label="Password requirements"
            aria-live="polite"
          >
            <RequirementItem met={lenOk} label={`Minimum ${MIN_PASSWORD_LEN} characters`} />
            {/* CHP-565: the row names the ENFORCED rule — a zxcvbn guessability
                score (>= MIN_PASSWORD_SCORE), not the spec mockup's literal
                numbers+symbols character classes. "correcthorsebatterystaple"
                passes; "P@ssw0rd" doesn't. The hint below the row carries
                zxcvbn's specific suggestion while unmet. */}
            <RequirementItem
              met={strengthOk}
              label="Hard to guess (avoid common words and patterns)"
              hint={pwHint}
            />
            <RequirementItem met={matchOk} label="Passwords must match" />
          </ul>
          <p className="text-xs text-muted-foreground/80">
            Tip: stringing together a few unrelated words is both strong and easy to remember.
          </p>
          {/* CHP-1080: the server's strength check ALSO seeds the account's
              own name/email, which this token-only page can't see (KNOWN
              RESIDUAL above; RACCPE-19 Option B is the full fix). Saying so
              up-front stops a server-side "too weak" rejection — and the
              burned single-use link — from being a surprise. */}
          <p className="text-xs text-muted-foreground/80" data-testid="set-pw-identity-note">
            Avoid building the password around your own name or email address — the server also
            checks for these and will reject it as too weak.
          </p>
        </div>

        <PasswordField
          id="set-pw2"
          label="Confirm password"
          value={pw2}
          onChange={setPw2}
          reveal={show2}
          onToggleReveal={() => setShow2((s) => !s)}
        />

        {topError && <FormError>{topError}</FormError>}

        {/* CHP-809: a 422 may have spent the single-use token; CHP-1195: a
            replayed link is dead by definition. Either way, offer a fresh
            link so the user can recover instead of being stuck. */}
        {showFreshLink && (
          <p className="text-sm text-muted-foreground">
            Need a new link? You can{" "}
            <Link to="/forgot-password" className="font-medium text-primary hover:underline">
              request a fresh one
            </Link>
            .
          </p>
        )}

        <Button
          type="submit"
          disabled={!allValid}
          loading={submit.isPending}
          loadingLabel="Saving…"
          size="lg"
          className="w-full"
        >
          Save & sign in
        </Button>
      </form>

      <p className="text-center text-sm">
        <Link to="/login" className="font-medium text-primary hover:underline">
          ← Back to sign in
        </Link>
      </p>
    </AuthShell>
  );
}

/**
 * One row of the live password-requirements checklist (onboarding friction
 * fix). The tick turns mint-green the moment the requirement is met; until then
 * it's a neutral outline circle. `hint` surfaces zxcvbn's specific suggestion
 * under the strength row while it's still unmet. The sr-only prefix gives
 * screen-reader users the met/not-met state the colour conveys visually.
 */
function RequirementItem({ met, label, hint }: { met: boolean; label: string; hint?: string }) {
  return (
    <li className="flex items-start gap-2">
      {met ? (
        <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden />
      ) : (
        <Circle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      )}
      <span className={met ? "text-success" : "text-muted-foreground"}>
        <span className="sr-only">{met ? "Requirement met: " : "Requirement not met: "}</span>
        {label}
        {!met && hint ? (
          <span className="mt-0.5 block text-muted-foreground/80">{hint}</span>
        ) : null}
      </span>
    </li>
  );
}

interface PasswordFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  reveal: boolean;
  onToggleReveal: () => void;
  autoFocus?: boolean;
  describedBy?: string;
}

/**
 * Password input with a show/hide eye toggle (CHP-254). The toggle button
 * sits inside the field; the input gets right padding so text never slides
 * under it. `aria-pressed` + a dynamic `aria-label` make the toggle
 * intelligible to screen readers.
 */
function PasswordField({
  id,
  label,
  value,
  onChange,
  reveal,
  onToggleReveal,
  autoFocus,
  describedBy,
}: PasswordFieldProps) {
  return (
    <div className="space-y-2">
      <Label
        htmlFor={id}
        className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground"
      >
        {label}
      </Label>
      <div className="relative">
        <Input
          id={id}
          type={reveal ? "text" : "password"}
          autoComplete="new-password"
          autoFocus={autoFocus}
          aria-required="true"
          aria-describedby={describedBy}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          minLength={MIN_PASSWORD_LEN}
          className="pr-10"
        />
        <button
          type="button"
          onClick={onToggleReveal}
          aria-pressed={reveal}
          aria-label={reveal ? "Hide password" : "Show password"}
          className="absolute inset-y-0 right-0 flex items-center rounded-md px-3 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/40"
        >
          {reveal ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

/**
 * Pull a human-readable message out of an error body. The set-password 422
 * sends `{ detail: "Password not strong enough…" }` (a string); guard for the
 * Pydantic-style `detail: [{ msg }]` array too in case schema validation
 * (min length) fires before the flow runs.
 */
function detailMessage(body: unknown): string | null {
  if (!body || typeof body !== "object") return null;
  const detail = (body as { detail?: unknown }).detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const msgs = detail
      .map((d) => (d && typeof d === "object" ? (d as { msg?: unknown }).msg : null))
      .filter((m): m is string => typeof m === "string" && m.trim().length > 0);
    if (msgs.length > 0) return msgs.join(" ");
  }
  return null;
}
